import { PLAN_BULK_MAX_IDS, type StaffRole, type TargetedPlanDto } from '@planuze/admin-contracts';
import { Button, ConfirmDialog, InlineAlert, TableSkeleton, toast } from '@planuze/ui';
import { useMemo, useState } from 'react';
import { useAdminLocale } from '../../content/locale-provider.js';
import { usePlansContent } from '../../content/sections/plans.js';
import { useLimitedBulkSelection } from '../../hooks/use-limited-bulk-selection.js';
import {
  useArchivePlan,
  useBulkArchivePlans,
  useTargetedPlansList,
} from '../../hooks/use-plans.js';
import { FacetMultiSelect } from '../filters/facet-multi-select.js';
import { SegmentedControl } from '../filters/segmented-control.js';
import { SearchInput } from '../search-input.js';
import { ArchivePlanDialog } from './archive-plan-dialog.js';
import { PlanEditModal } from './plan-edit-modal.js';
import { planErrorMessage } from './plan-error.js';
import { matchesQuery } from './plan-search.js';
import { TargetedPlanBulkBar } from './targeted-plan-bulk-bar.js';
import { isTargetedPlanExpired, TargetedPlansTable } from './targeted-plans-table.js';

/* Aba Negociados (ADR-0435): plano completo em modo direcionado (alvo + validade + motivo). A
   criação é uma página cheia com revisão; metadata mutável é editada na própria lista (ADR-0888).
   A lista é paginada por
   cursor (ADR-0713); os filtros são client-side (ADR-0704) sobre as PÁGINAS JÁ CARREGADAS — busca
   + alvo (org/usuário) + status (ativo/expirado, mesma regra `isTargetedPlanExpired` da tabela) +
   tier (faceta). "Carregar mais" traz a próxima página. */

type TargetFilter = 'all' | 'org' | 'user';
type StatusFilter = 'all' | 'active' | 'expired';

export function TargetedSection({
  active,
  canManage,
  staffRole,
}: {
  readonly active: boolean;
  readonly canManage: boolean;
  readonly staffRole: StaffRole;
}) {
  const copy = usePlansContent();
  const { locale } = useAdminLocale();
  /* Lazy (ADR-0704): a query só dispara quando a aba está ativa. */
  const { data, isLoading, isError, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useTargetedPlansList(active);
  const archive = useArchivePlan();
  const bulkArchive = useBulkArchivePlans();
  const [toArchive, setToArchive] = useState<TargetedPlanDto | null>(null);
  const [toEdit, setToEdit] = useState<TargetedPlanDto | null>(null);
  const [pendingBulkIds, setPendingBulkIds] = useState<readonly string[] | null>(null);
  const [search, setSearch] = useState('');
  const [tiers, setTiers] = useState<string[]>([]);
  const [target, setTarget] = useState<TargetFilter>('all');
  const [status, setStatus] = useState<StatusFilter>('all');

  const confirmArchive = (): void => {
    if (toArchive === null) return;
    archive.mutate(toArchive.id, {
      onSuccess: () => {
        toast.success(copy.targetedSection.archivedToast);
        setToArchive(null);
      },
      onError: (err) => toast.error(planErrorMessage(err, locale)),
    });
  };

  const plans = useMemo(() => data?.pages.flatMap((page) => page.items) ?? [], [data]);
  const tierOptions = useMemo(
    () =>
      Array.from(new Set(plans.map((p) => p.tier)))
        .sort((a, b) => a.localeCompare(b))
        .map((t) => ({ value: t, label: t })),
    [plans],
  );

  const query = search.trim().toLowerCase();
  const filtered = useMemo(() => {
    const now = new Date();
    return plans.filter((plan) => {
      if (query !== '' && !matchesQuery(plan, query)) return false;
      if (tiers.length > 0 && !tiers.includes(plan.tier)) return false;
      if (target === 'org' && plan.orgId === null) return false;
      if (target === 'user' && plan.userId === null) return false;
      const expired = isTargetedPlanExpired(plan, now);
      if (status === 'active' && expired) return false;
      if (status === 'expired' && !expired) return false;
      return true;
    });
  }, [plans, query, tiers, target, status]);
  const visibleIds = useMemo(() => filtered.map((plan) => plan.id), [filtered]);
  const selection = useLimitedBulkSelection(visibleIds, PLAN_BULK_MAX_IDS);
  const selectedPlans = useMemo(
    () => filtered.filter((plan) => selection.selectedIds.has(plan.id)),
    [filtered, selection.selectedIds],
  );

  const hasFilters = query !== '' || tiers.length > 0 || target !== 'all' || status !== 'all';
  const clearFilters = (): void => {
    setSearch('');
    setTiers([]);
    setTarget('all');
    setStatus('all');
  };

  /* Contadores por opção sobre a lista carregada (ADR-0704). */
  const targetCounts = useMemo(
    () => ({
      all: plans.length,
      org: plans.filter((p) => p.orgId !== null).length,
      user: plans.filter((p) => p.userId !== null).length,
    }),
    [plans],
  );
  const statusCounts = useMemo(() => {
    const now = new Date();
    const expired = plans.filter((p) => isTargetedPlanExpired(p, now)).length;
    return { all: plans.length, active: plans.length - expired, expired };
  }, [plans]);

  return (
    <div className="space-y-4">
      <InlineAlert variant="info">{copy.targetedSection.intro}</InlineAlert>
      <div className="flex flex-wrap items-center gap-3">
        <SearchInput
          value={search}
          onChange={setSearch}
          ariaLabel={copy.targetedSection.searchAriaLabel}
          placeholder={copy.common.searchPlaceholder}
        />
        <SegmentedControl<TargetFilter>
          value={target}
          onChange={setTarget}
          ariaLabel={copy.targetedSection.targetFilterAriaLabel}
          options={[
            { value: 'all', label: copy.common.all, count: targetCounts.all },
            { value: 'org', label: copy.targetedSection.targetOrg, count: targetCounts.org },
            { value: 'user', label: copy.targetedSection.targetUser, count: targetCounts.user },
          ]}
        />
        <SegmentedControl<StatusFilter>
          value={status}
          onChange={setStatus}
          ariaLabel={copy.common.statusFilterAriaLabel}
          options={[
            { value: 'all', label: copy.common.all, count: statusCounts.all },
            { value: 'active', label: copy.targetedTable.statusActive, count: statusCounts.active },
            {
              value: 'expired',
              label: copy.targetedTable.statusExpired,
              count: statusCounts.expired,
            },
          ]}
        />
        <FacetMultiSelect
          label={copy.common.tierFilterLabel}
          values={tiers}
          onChange={setTiers}
          options={tierOptions}
        />
      </div>
      {canManage && (
        <TargetedPlanBulkBar
          selectedCount={selectedPlans.length}
          busy={bulkArchive.isPending}
          onArchive={() =>
            setPendingBulkIds(selectedPlans.slice(0, PLAN_BULK_MAX_IDS).map((plan) => plan.id))
          }
          onClear={selection.clear}
        />
      )}
      {isError ? (
        <InlineAlert variant="destructive">{copy.targetedSection.loadError}</InlineAlert>
      ) : isLoading || data === undefined ? (
        <TableSkeleton columns={canManage ? 8 : 6} />
      ) : (
        <TargetedPlansTable
          plans={filtered}
          staffRole={staffRole}
          onEdit={setToEdit}
          onArchive={setToArchive}
          selectedIds={selection.selectedIds}
          onToggle={selection.toggle}
          onToggleAll={selection.toggleAll}
          hasFilters={hasFilters}
          onClearFilters={clearFilters}
        />
      )}
      {hasNextPage && (
        <div className="flex justify-center">
          <Button
            variant="ghost"
            loading={isFetchingNextPage}
            onClick={() => {
              fetchNextPage().catch(() => undefined);
            }}
          >
            {copy.common.loadMore}
          </Button>
        </div>
      )}
      <ArchivePlanDialog
        open={toArchive !== null}
        onOpenChange={(next) => {
          if (!next) setToArchive(null);
        }}
        isPending={archive.isPending}
        title={copy.targetedSection.archiveDialogTitle}
        description={copy.targetedSection.archiveDialogDescription(toArchive?.lookupKey ?? '')}
        onConfirm={confirmArchive}
      />
      {toEdit !== null && <PlanEditModal open plan={toEdit} onClose={() => setToEdit(null)} />}
      <ConfirmDialog
        open={pendingBulkIds !== null}
        onOpenChange={(open) => {
          if (!open) setPendingBulkIds(null);
        }}
        title={copy.targetedSection.bulkTitle}
        description={copy.targetedSection.bulkDescription(pendingBulkIds?.length ?? 0)}
        confirmLabel={copy.targetedSection.bulkConfirm}
        variant="destructive"
        mapError={(error) => planErrorMessage(error, locale)}
        onConfirm={async () => {
          if (pendingBulkIds === null || pendingBulkIds.length === 0) return;
          const response = await bulkArchive.mutateAsync(pendingBulkIds);
          const failed = response.results.filter((result) => !result.ok);
          if (failed.length === 0) {
            toast.success(copy.targetedSection.bulkSuccess(response.results.length));
          } else {
            toast.warning(
              copy.targetedSection.bulkPartial(
                response.results.length - failed.length,
                failed.length,
              ),
            );
          }
          selection.replace(failed.map((result) => result.id));
          setPendingBulkIds(null);
        }}
      />
    </div>
  );
}
